import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useQrCodeRenewal } from '../hooks/useQrCodeRenewal';
import { useOrderStatusPolling } from '../hooks/useOrderStatusPolling';
import {
  AlertBoxContainer,
  Wrapper,
  Title,
  Container,
  PendingContainer
} from '../styles/components';
import AlertBox from 'shared/components/presentational/AlertBox/AlertBox';
import Loader from 'shared/components/presentational/Loader';
import { FormattedProps } from '../types';
import Raw from 'shared/components/presentational/Raw/Raw';
import { RenewalFormState } from '../../reducer';
import { UpdatingOptionsState } from 'shared/blocks/one-shop/updating-options/reducer';
import { useUpdatingOptionsOfferModification } from '../hooks/useUpdatingOptionsOfferModification';

export default function Payment({
  success,
  failure,
  pending,
  apis: {
    getQrCodeRenewal,
    getUpdatingOptionsOfferModification,
    getQrCodeOrderStatus
  }
}: FormattedProps) {
  const { t } = useTranslation();

  const [orderId, setOrderId] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  const renewalFormValues = useSelector(
    (state: { renewalForm: RenewalFormState }) => state.renewalForm
  );
  const updatingOptionsValues = useSelector(
    (state: { updatingOptions: UpdatingOptionsState }) =>
      state.updatingOptions || {}
  );

  const {
    mutate: initiateQrCodeRenewal,
    isError: isQrCodeRenewalError,
    isLoading: isQrCodeRenewalLoading
  } = useQrCodeRenewal({
    renewalFormValues,
    getQrCodeRenewal,
    saleDeviceId: renewalFormValues.saleDeviceId
  });

  const {
    mutate: initiateUpdatingOptions,
    isError: isUpdatingOptionsError,
    isLoading: isUpdatingOptionsLoading
  } = useUpdatingOptionsOfferModification({
    updatingOptionsValues,
    getUpdatingOptionsOfferModification
  });

  const {
    data: orderStatus,
    isError: pollingError,
    isLoading: isPollingLoading
  } = useOrderStatusPolling({
    getQrCodeOrderStatus,
    orderId,
    token: renewalFormValues.token
      ? `QrcodeBearer ${renewalFormValues.token}`
      : `AuthBypass ${updatingOptionsValues.headers.token}`,
    correlationId
  });

  useEffect(() => {
    initiateQrCodeRenewal(undefined, {
      onSuccess: data => {
        if (data) {
          setOrderId(data.orderId), setCorrelationId(data.correlationId);
        }
      }
    });
    initiateUpdatingOptions(undefined, {
      onSuccess: data => {
        if (data) {
          setOrderId(data.orderId), setCorrelationId(data.correlationId);
        }
      }
    });
  }, []);

  if (isQrCodeRenewalError || isUpdatingOptionsError || pollingError) {
    if (getQrCodeRenewal) {
      return (
        <AlertBoxContainer>
          <AlertBox
            status="error"
            content={<Trans i18nKey="blocks.renewal.qrCodeScanError" />}
          />
        </AlertBoxContainer>
      );
    }
    return (
      <AlertBoxContainer>
        <AlertBox
          status="error"
          title={t('blocks.renewal.getUpdatingOptionsOfferModification.title')}
          content={t(
            'blocks.renewal.getUpdatingOptionsOfferModification.content'
          )}
        />
      </AlertBoxContainer>
    );
  }

  const statusComponents: Record<string, JSX.Element | null> = {
    CONFIRMED: success ? (
      <StatusDisplay title={success.title}>
        <AlertBox
          status={success.content.status}
          content={success.content.content}
          link={
            success.content.cta
              ? {
                  label: success.content.cta.label,
                  href: success.content.cta.href
                }
              : undefined
          }
        />
      </StatusDisplay>
    ) : null,
    FAILED: failure ? (
      <StatusDisplay title={failure.title}>
        <AlertBox
          status={failure.content.status}
          content={failure.content.content}
          link={
            failure.content.cta
              ? {
                  label: failure.content.cta.label,
                  href: failure.content.cta.href
                }
              : undefined
          }
        />
      </StatusDisplay>
    ) : null,
    CREATED: pending ? (
      <StatusDisplay title={pending.title}>
        <PendingContainer>
          <div>
            <Loader />
          </div>
          {pending.content && <Raw html={pending.content} />}
        </PendingContainer>
      </StatusDisplay>
    ) : null
  };

  if (isQrCodeRenewalLoading || isUpdatingOptionsLoading || isPollingLoading) {
    return statusComponents['CREATED'];
  }

  return orderStatus ? statusComponents[orderStatus] : null;
}

const StatusDisplay = ({
  title,
  children
}: {
  title: string | null;
  children: React.ReactNode;
}) => (
  <Wrapper>
    <Container>
      {title && <Title>{title}</Title>}
      {children}
    </Container>
  </Wrapper>
);
